/**
 * @typedef {{[key: string]: string}} Options
 * @typedef {{handle: string; name: string; confidence: number; options: Options}} Language
 * @typedef {{handle: string; name: string; confidence: number; options: { [key: string]: string }}} PartsCountry
 * @typedef {{country:  PartsCountry; language: Language}} Parts
 * @typedef {{parts: Parts; confidence: number}} Suggestion
 * @typedef {{duties_collected: boolean}} Features
 * @typedef {{handle: string; name: string}} DetectedValuesCountry
 * @typedef {{country_name: string; country: DetectedValuesCountry}} DetectedValues
 * @typedef {{detected_values: DetectedValues; features: Features; suggestions: Suggestion[]}} GeolocalizationResponse
 */

class FFPopupGeolocation extends HTMLElement {
  /**
   * @type {HTMLDivElement}
   */
  #initialContainer;

  /**
   * @type {HTMLDivElement}
   */
  #marketsContainer;

  /**
   * @type {HTMLDivElement}
   */
  #popup;

  /**
   * @type {string}
   */
  #blockId;

  /**
   * @type {string}
   */
  #customerRegion;

  /**
   * @type {string}
   */
  #geolocationCountry = "";

  /**
   * @type {string}
   */
  #suggestedLanguage = "";

  /**
   * @type {HTMLButtonElement | null}
   */
  #acceptBtn = null;

  /**
   * @type {HTMLButtonElement | null}
   */
  #cancelBtn = null;

  constructor() {
    super();

    const blockId = this.dataset.blockId;
    const customerRegion = this.dataset.customerRegion;

    setTimeout(() => {
      /**
       * @type {HTMLDivElement | null}
       */
      const popup = this.querySelector(`#ff-container_${blockId}`);

      /**
       * @type {HTMLDivElement | null}
       */
      const initialContainer = this.querySelector(
        `#ff-container_initial_${blockId}`
      );

      /**
       * @type {HTMLDivElement | null}
       */
      const marketsContainer = this.querySelector(
        `#ff-container_markets_${blockId}`
      );

      if (
        !initialContainer ||
        !marketsContainer ||
        !popup ||
        !blockId ||
        !customerRegion
      ) {
        console.error("Fantastic Geolocalization could not be initialized.");

        return;
      }

      this.#initialContainer = initialContainer;
      this.#marketsContainer = marketsContainer;
      this.#popup = popup;
      this.#blockId = blockId;
      this.#customerRegion = customerRegion;

      const listenersSettled = this.#setEventListeners();

      if (listenersSettled) {
        this.init()
          .then(() =>
            console.log(
              "Fantastic Geolocalization was initialized successfully."
            )
          )
          .catch(() =>
            console.error("Fantastic Geolocalization could not be initialized.")
          );
      }
    }, 300);
  }

  #setEventListeners() {
    const closeButton = this.querySelector(`#ff-close_btn_${this.#blockId}`);
    const changeMarket = this.querySelector(
      `#ff-change-market_${this.#blockId}`
    );
    const cancelChange = this.querySelector(
      `#ff-cancel-change_${this.#blockId}`
    );
    const acceptChange = this.querySelector(
      `#ff-accept-change_${this.#blockId}`
    );
    const continueButton = this.querySelector(`#ff-continue_${this.#blockId}`);

    if (
      !changeMarket ||
      !cancelChange ||
      !acceptChange ||
      !continueButton
    ) {
      console.error("Fantastic Geolocalization could not be initialized.");

      return false;
    }

    this.#acceptBtn = acceptChange;
    this.#cancelBtn = cancelChange;

    this.addEventListener("click", () => this.#closePopup());
    this.#popup.addEventListener("click", (e) => e.stopPropagation());
    closeButton?.addEventListener("click", () => {
      this.#closePopup();
      this.#setOptionCookie();
    });
    changeMarket.addEventListener("click", () => this.#showMarketsForm());
    cancelChange.addEventListener("click", () => this.#showInitialForm());
    acceptChange.addEventListener("click", () => {
      const titleSelectorEl = this.querySelector(
        `#ff-select_markets_${this.#blockId}`
      );

      if (!titleSelectorEl) return;

      const option =
        titleSelectorEl.dataset.selected || this.#geolocationCountry;

      if (option !== this.#geolocationCountry) {
        this.#setOptionCookie();
      }

      this.#confirmSelection(option);
    });
    continueButton.addEventListener("click", () =>
      this.#confirmSelection(this.#geolocationCountry)
    );

    return true;
  }

  #closePopup() {
    this.classList.add("hide-animated");
    setTimeout(() => this.#hide(this), 1000);
  }

  /**
   * @param {HTMLElement} elm Element to hide.
   * @description Add a ``d-none`` class to the element in order to hide it by css.
   */
  #hide(elm) {
    elm.classList.add("d-none");
    elm.style.display = "none";
  }

  /**
   * @param {HTMLElement} elm Element to show
   * @description Removes the``d-none`` class from the element in order to show it.
   */
  #show(elm) {
    elm.classList.remove("d-none");
    elm.style.display = "block";
  }

  /**
   * @description Show initial form and hide regions form.
   */
  #showInitialForm() {
    this.#hide(this.#marketsContainer);
    this.#show(this.#initialContainer);
  }

  #showMarketsForm() {
    this.#show(this.#marketsContainer);
    this.#hide(this.#initialContainer);
  }

  #setOptionCookie() {
    const expiryDate = new Date();

    expiryDate.setTime(expiryDate.getTime() + 2 * 24 * 60 * 60 * 1000);

    document.cookie = `ignore_localization_ff=1;expires=${expiryDate.toUTCString()};path=/`;
  }

  #getOptionCookie() {
    let name = `ignore_localization_ff=`;

    let decodedCookie = decodeURIComponent(document.cookie);
    let ca = decodedCookie.split(";");

    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];

      while (c.charAt(0) == " ") {
        c = c.substring(1);
      }

      if (c.indexOf(name) == 0) {
        return Number(c.substring(name.length, c.length) || "0");
      }
    }

    return 0;
  }

  /**
   * @param {string} country The country selected
   **/
  #confirmSelection(country) {
    const formId = new Date().valueOf().toString();

    const formHtml = `
      <form id="${formId}" action="/localization" method="POST" hidden>
        <input name="_method" value="PUT">
        <input name="country_code" value="${country}">
        <input name="language_code" value="${this.#suggestedLanguage}">
      </form>
    `;

    document.body.insertAdjacentHTML("beforeend", formHtml);

    // @ts-ignore
    document.getElementById(formId)?.submit();
  }

  async #getLocalization() {
    const res = await fetch(
      // @ts-ignore
      `${window.Shopify.routes.root}browsing_context_suggestions.json?country[enabled]=true&language[enabled]=true&currency[enabled]=true`
    );

    /**
     * @type {GeolocalizationResponse}
     */
    const data = await res.json();

    return data;
  }

  async init() {
    try {
      const countrySelectorEl = this.querySelector(
        `#ff-select_markets_${this.#blockId}`
      );
      const titleSelectorEl = this.querySelector(`#ff-title_${this.#blockId}`);
      const contentEl = this.querySelector(`#ff-content_${this.#blockId}`);

      if (!countrySelectorEl || !titleSelectorEl) throw new Error();

      const optionCookie = this.#getOptionCookie();

      const {
        detected_values: {
          country: { handle, name: country_name },
        },
        suggestions: [
          {
            parts: {
              country: { options } = {
                options: {},
              },
              language: { handle: languageHandle, name: languageName } = {
                handle: "",
                name: "",
              },
              currency: { handle: currencyHandle = { handle: "" } },
            },
          } = { parts: { country: { options: {} }, language: { handle: "" } } },
        ] = [
          {
            parts: {
              country: { name: "", options: {} },
              language: { handle: "", name: "" },
              currency: { handle: "" },
            },
          },
        ],
      } = await this.#getLocalization();

      this.#geolocationCountry = handle;
      this.#suggestedLanguage = languageHandle;

      let title = titleSelectorEl.innerHTML;
      title = title.replace(/\[\[country_name\]\]/g, `<strong>${country_name}</strong>`);
      titleSelectorEl.innerHTML = title;

      let content = contentEl.innerHTML;
      content = content
        .replaceAll("[[country_name]]", country_name)
        .replaceAll("[[language_code]]", languageName)
        .replaceAll("[[currency_code]]", currencyHandle);
      contentEl.innerHTML = content;

      this.querySelector("#ff-country-img")?.setAttribute(
        "src",
        `${window.Shopify.routes.root}cdn/shop/files/${handle}.png?width=140`
      );

      const countryOptions = Object.entries(options);

      countryOptions.sort(([_, a], [__, b]) => {
        if (a.toLowerCase() < b.toLowerCase()) return -1;
        if (a.toLowerCase() > b.toLowerCase()) return 1;
        else return 0;
      });

      const listEl = countrySelectorEl.querySelector(".ff-geo-collapsible__list");
      const valueEl = countrySelectorEl.querySelector(".ff-geo-collapsible__value");
      const toggleEl = countrySelectorEl.querySelector(".ff-geo-collapsible__toggle");
      const optionTemplateEl = this.querySelector(`#ff-option-template_${this.#blockId}`);

      if (valueEl) valueEl.textContent = countrySelectorEl.dataset.placeholder || "";
      this.#acceptBtn?.setAttribute("disabled", "");
      this.#cancelBtn?.setAttribute("disabled", "");

      countryOptions.forEach(([countryCode, countryName]) => {
        let optionEl;

        if (optionTemplateEl) {
          const clone = optionTemplateEl.content.cloneNode(true);
          optionEl = clone.firstElementChild;
          optionEl.setAttribute("type", "button");
          const textEl = optionEl.querySelector(".ff-geo-collapsible__option-text");
          if (textEl) textEl.textContent = countryName;
        } else {
          optionEl = document.createElement("button");
          optionEl.type = "button";
          optionEl.className = "ff-geo-collapsible__option";
          optionEl.textContent = countryName;
        }

        optionEl.dataset.value = countryCode;

        if (countryCode === this.#customerRegion) {
          optionEl.classList.add("is-selected");
        }

        optionEl.addEventListener("click", () => {
          listEl?.querySelectorAll(".ff-geo-collapsible__option").forEach((el) =>
            el.classList.remove("is-selected")
          );
          optionEl.classList.add("is-selected");
          if (valueEl) valueEl.textContent = countryName;
          countrySelectorEl.dataset.selected = countryCode;
          countrySelectorEl.setAttribute("aria-expanded", "false");
          this.#acceptBtn?.removeAttribute("disabled");
          this.#cancelBtn?.removeAttribute("disabled");
        });

        listEl?.appendChild(optionEl);
      });

      toggleEl?.addEventListener("click", () => {
        const isExpanded = countrySelectorEl.getAttribute("aria-expanded") === "true";
        countrySelectorEl.setAttribute("aria-expanded", String(!isExpanded));
      });

      if (this.#popup.offsetHeight < window.innerHeight) {
        this.#popup.classList.add("centered"); // center the popup in case of being smaller than the screen
      }

      this.#popup.style.opacity = "1"; //animation for showing the popup the first time

      this.#showInitialForm();

      if (this.#customerRegion !== handle && !optionCookie) {
        this.#show(this);
      }

      // @ts-ignore
      window.FfGeoPopUp = {
        show: () => {
          this.classList.remove("hide-animated");

          this.#show(this);

          setTimeout(() => this.#showMarketsForm(), 100);
        },
        hide: () => {
          this.#hide(this);
        },
      };
    } catch (error) {
      console.log(error);
    }
  }
}

if (!customElements.get("ff-popup-geolocation"))
  customElements.define("ff-popup-geolocation", FFPopupGeolocation);

